import { useState, useEffect, useCallback, useRef } from "react";
import ReactDataSheet from "react-datasheet";
import "react-datasheet/lib/react-datasheet.css";
import "./TariffDataSheet.css";
import { periodColors, DEMAND_NOTICE, useDemandUnits } from "./TarifUtils";
import { SeChip } from "@se/web-ui-react";
import { uniqueId } from "lodash";
import { useAtom } from "jotai";
import {
  useMantineColorScheme,
  Select,
  Modal,
} from "@mantine/core";
import PropTypes from "prop-types";
import { IconDotsVertical } from "@tabler/icons-react";
import { useDualTranslation } from "../utils/i18nHelpers";
import ProjectNumberInput from "../components/common/ProjectNumberInput";
import { projectDetailsAtom } from "../utils/atoms/projectDetailsAtom";
import { formatProjectNumber } from "../utils/helpers";

const TariffDataSheet = ({
  activeDemandType,
  seasonsList,
  selectedSeason,
  activeDemandTypeIndex,
  updateTier,
  updateTimeSlot,
  callSnackbar,
  updatePeriodName,
  handleRemoveTimeSlots,
  currentDate,
  updateTierFractionInDayGroup,
  updateSelectedSeasonAndDayGroup,
  allowBill,
  allowPenalty,
  batchUpdateTierFractionsInDayGroup,
  batchUpdateRachetGlobally,
  updatePeriodColor,
  isMasterContext,
  isReadOnly,
}) => {
  const { t } = useDualTranslation();
  const DEMAND_UNITS = useDemandUnits();
  const [projectDetails] = useAtom(projectDetailsAtom);
  const numberFormat = projectDetails?.projectDTO?.numberFormat;

  const formatNumericCell = (value) => {
    if (typeof value !== "string" && typeof value !== "number") return value;
    const text = String(value);
    const match = text.match(/^([+-]?\d+)(?:\.(\d+))?$/);
    if (!match) return value;
    const fractionDigits = match[2]?.length || 0;
    return formatProjectNumber(value, {
      numberFormat,
      minimumFractionDigits: fractionDigits,
      maximumFractionDigits: fractionDigits,
    });
  };
  
  const [grid, setGrid] = useState([]);
  const [selectedTime, setSelectedTime] = useState([]);
  const [dynamicClass, setDynamicClass] = useState("data-grid-100");
  const { colorScheme } = useMantineColorScheme();
  const [headerTierMenu, setHeaderTierMenu] = useState({
    opened: false,
    tierId: null,
    anchor: null,
  });
  const [colorModalOpen, setColorModalOpen] = useState(false);
  const [colorModalRow, setColorModalRow] = useState(null);
  const [tempColor, setTempColor] = useState("#ffffff");

  // Add a ref for the horizontal scroller
  const scrollerRef = useRef(null);
  const prevTierCountRef = useRef(
    activeDemandType.rate[0].options.tierBoundaries.length
  );
  const generateTimeOptions = () => {
    const options = [];
    for (let hour = 0; hour < 24; hour++) {
      for (let minute = 0; minute < 60; minute += 15) {
        const formattedHour = hour.toString().padStart(2, "0");
        const formattedMinute = minute.toString().padStart(2, "0");
        options.push(`${formattedHour}:${formattedMinute}`);
      }
    }
    options.push("24:00");
    return options;
  };

  const timeOptions = generateTimeOptions();

  // Find all used start and end times (as string) for all slots
  const usedStartTimes = new Set();
  const usedEndTimes = new Set();
  activeDemandType.rate.forEach((tariff) => {
    tariff.timeSlots.forEach((slot) => {
      const start = `${slot.startTime.hour.toString().padStart(2, "0")}:${slot.startTime.minute.toString().padStart(2, "0")}`;
      const end = `${slot.endTime.hour.toString().padStart(2, "0")}:${slot.endTime.minute.toString().padStart(2, "0")}`;
      usedStartTimes.add(start);
      usedEndTimes.add(end);
    });
  });

  // Filter out 24:00 from timeOptions if it is already used as an end time in any period for this demand type
  let filteredTimeOptions = timeOptions;
  const is24Used = activeDemandType.rate.some((tariff) =>
    tariff.timeSlots.some((slot) =>
      slot.endTime.hour === 24 && slot.endTime.minute === 0
    )
  );
  if (is24Used) {
    filteredTimeOptions = timeOptions.filter((t) => t !== "24:00");
  }

  useEffect(() => {
    initalRenderer();
  }, [activeDemandType, activeDemandType?.rate, selectedTime, currentDate]);

  useEffect(() => {
    const tableCells = document.querySelectorAll(".data-grid td");
    if (colorScheme === "dark") {
      tableCells.forEach((cell) => {
        cell.classList.add("dark-col");
      });
    } else {
      tableCells.forEach((cell) => {
        cell.classList.remove("dark-col");
      });
    }
  }, [colorScheme]);

  const calculateClassName = (tierCount) => {
    if (tierCount <= 2) return "data-grid-100";
    return `data-grid-${100 + (tierCount - 2) * 25}`;
  };

  const handleRemoveTimeSlot = (index, tariffId) => {
    handleRemoveTimeSlots(index, tariffId);
    setSelectedTime([]); // Clear selected time to force re-render
    initalRenderer();
  };

  const initalRenderer = useCallback(() => {
    const tierCount = activeDemandType.rate[0].options.tierBoundaries.length;
    const calculatedClass = calculateClassName(tierCount);
    setDynamicClass(calculatedClass);

    const stickyCol1 = tierCount > 1 ? "sticky-col1" : "";
    const stickyCol2 = tierCount > 1 ? "sticky-col2" : "";
    const stickyCol3 = tierCount > 1 ? "sticky-col3" : "";

    const headers = [
      {
        value: "",
        readOnly: true,
        width: "10%",
        className: ` ${stickyCol1} ${colorScheme === "dark" ? "dark-col" : ""}`,
      },
      {
        value: t("demandCharge.dataSheet.duration"),
        colSpan: 2,
        className: `cell-with-comment ${stickyCol2} ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        width: "18%",
        valueViewer: (props) => (
          <>
            <span className="font-header-cell">{props.value}</span>
          </>
        ),
      },
      ...activeDemandType.rate[0].options.tierBoundaries.map((tier, index) => {
        const tierKeys = Object.keys(tier).filter((key) => key !== "tierId");
        return {
          value: (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <span style={{ fontSize: 16, fontWeight: 600 }}>
                {t("demandCharge.dataSheet.tierLabel", {
                  index: index + 1,
                })}
              </span>
             
            </div>
          ),
          tierId: tier.tierId,
          colSpan: tierKeys.length,
          className: `cell-with-comment ${
            colorScheme === "dark" ? "dark-col" : ""
          }`,
          readOnly: true,
          disableEvents: false,
          type: "tier",
          width: "30%",
        };
      }),
    ];
    const subHeaders = [
      {
        value: "Time Periods",
        readOnly: true,
        className: `cell-with-comment ${stickyCol1} ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },
      {
        value: "Start Time",
        className: `cell-with-comment ${stickyCol2} ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },
      {
        value: t("demandCharge.dataSheet.to"),
        className: `cell-with-comment ${stickyCol3} ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        readOnly: true,
        disableEvents: true,
        valueViewer: (props) => (
          <>
            <span className="font-cell">{props.value}</span>
          </>
        ),
      },
      ...activeDemandType.rate[0].options.tierBoundaries.flatMap((tier) => {
        return Object.keys(tier)
          .filter((key) => key !== "tierId")
          .map((key) => ({
            value:
              `${t(`demandCharge.notice.${key}`)} ${DEMAND_UNITS?.[key] || ""}`.trim(),
            className: `cell-with-comment  ${
              colorScheme === "dark" ? "dark-col" : ""
            }`,
            readOnly: true,
            disableEvents: true,
            valueViewer: (props) => (
              <>
                <span className="font-cell">{props.value}</span>
              </>
            ),
          }));
      }),
    ];
    const rows = activeDemandType.rate.map((tariff, rowIndex) => [
      {
        value: tariff.name,
        type: "tariffName",
        className: `background-transparent ${stickyCol1} ${
          colorScheme === "dark" ? "dark-col" : ""
        }`,
        valueViewer: (props) => (
             <>
                      <span className="font-cell period-name">{props.value}</span>
                      <span
                        className="label-circle"
                        style={{ background: `${periodColors[rowIndex]}` }}
                      ></span>
                    </>
        ),
      },
      {
        value: (
          <div>
            <div className="time-select-container">
              <Select
                key={uniqueId()}
                data={filteredTimeOptions.map((option) => ({
                  value: option,
                  label: option,
                  disabled:
                    disabledTimes.has(option) || allSlotStartTimes.has(option),
                }))}
                value={selectedTime[rowIndex]?.startTime || filteredTimeOptions[0]}
                onChange={(value) =>
                  handleTimeChange(rowIndex, "startTime", value)
                }
                size="xs"
                style={{ width: 90, marginRight: 8 }}
                placeholder={t("demandCharge.dataSheet.start")}
                searchable={true}
                nothingFound={t("demandCharge.dataSheet.noOptions")}
                withinPortal
              />
              <Select
                key={uniqueId()}
                data={filteredTimeOptions.filter(option => !allSlotEndTimes.has(option)).map((option) => ({
                  value: option,
                  label: option,
                  // Disable if in disabledTimes, or if option is 00:00 (hour 0)
                  disabled: disabledTimes.has(option) || option === "00:00",
                }))}
                value={selectedTime[rowIndex]?.endTime || filteredTimeOptions[0]}
                onChange={(value) =>
                  handleTimeChange(rowIndex, "endTime", value)
                }
                size="xs"
                style={{ width: 90, marginRight: 8 }}
                placeholder={t("demandCharge.dataSheet.end")}
                searchable={true}
                nothingFound={t("demandCharge.dataSheet.noOptions")}
                withinPortal
              />
              <se-button
                color="standard"
                style={{ width: "50px" }}
                aria-label={`add-demand-timeslot-row-${rowIndex}`}
                onClick={() => handleAddTimeSlot(rowIndex)}
              >
                {t("demandCharge.dataSheet.add")}
              </se-button>
            </div>
            <div className="chip">
              {tariff.timeSlots.length > 0 &&
                tariff.timeSlots.map((slot, index) => (
                  <SeChip
                    size="small"
                    key={index}
                    aria-label={`demand-timeslot-chip-row-${rowIndex}-index-${index}`}
                    value={`${slot.startTime.hour}:${slot.startTime.minute} - ${slot.endTime.hour}:${slot.endTime.minute}`}
                    can-close={!isReadOnly}
                    onDidClose={() =>
                      handleRemoveTimeSlot(index, tariff.tariffId)
                    }
                  ></SeChip>
                ))}
            </div>
          </div>
        ),
        colSpan: 2,
        readOnly: true,
        disableEvents: true,
        className: `${stickyCol2} ${colorScheme === "dark" ? "dark-col" : ""}`,
      },
      ...tariff.options.tierBoundaries.flatMap((tier) => {
        return Object.keys(tier)
          .filter((key) => key !== "tierId")
          .map((key) => ({
            value: tier[key]?.value ?? "",
            tariffId: tariff.tariffId,
            tierId: tier.tierId,
            type: key,
            readOnly: false,
          }));
      }),
    ]);
    const gridRows = [subHeaders, ...rows];
    if (tierCount > 1) {
      gridRows.unshift(headers);
    }
    setGrid(gridRows);

    // Smooth transition when new tier is added
    if (tierCount > prevTierCountRef.current) {
      setTimeout(() => {
        if (scrollerRef.current) {
          scrollerRef.current.scrollLeft = scrollerRef.current.scrollWidth;
        }
        // Highlight last tier header
        const headerRow = document.querySelector(".data-grid tr");
        if (headerRow) {
          const lastHeaderCell =
            headerRow.querySelectorAll("td")[headerRow.children.length - 1];
          if (lastHeaderCell) {
            lastHeaderCell.classList.add("highlight-tier");
            setTimeout(() => {
              lastHeaderCell.classList.remove("highlight-tier");
            }, 1200);
          }
        }
      }, 100);
    }
    prevTierCountRef.current = tierCount;
  }, [
    activeDemandType,
    selectedTime,
    currentDate,
    colorScheme,
    allowBill,
    allowPenalty,
  ]);

  const handleAddTimeSlot = (periodIndex) => {
    const startTime = selectedTime[periodIndex]?.startTime || timeOptions[0];
    const endTime = selectedTime[periodIndex]?.endTime || timeOptions[0];
    const [startHour, startMinute] = startTime.split(":").map(Number);
    const [endHour, endMinute] = endTime.split(":").map(Number);

    const newStartTime = { hour: startHour, minute: startMinute };
    const newEndTime = { hour: endHour, minute: endMinute };

    // Validate if the new time slot is in reverse order
    if (
      newStartTime.hour > newEndTime.hour ||
      (newStartTime.hour === newEndTime.hour &&
        newStartTime.minute >= newEndTime.minute)
    ) {
      callSnackbar("error", "start / end time are incorrect");

      return;
    }

    // Validate if the new time slot overlaps with any existing time slots
    const isOverlapping = activeDemandType.rate.some((tariff) =>
      tariff.timeSlots.some(
        (slot) =>
          (newStartTime.hour < slot.endTime.hour ||
            (newStartTime.hour === slot.endTime.hour &&
              newStartTime.minute < slot.endTime.minute)) &&
          (newEndTime.hour > slot.startTime.hour ||
            (newEndTime.hour === slot.startTime.hour &&
              newEndTime.minute > slot.startTime.minute))
      )
    );

    if (isOverlapping) {
      callSnackbar("error", "Time slots overlapped");
      return;
    }
    updateTimeSlot(periodIndex, newStartTime, newEndTime);
    setSelectedTime([], (prevState) => {
      initalRenderer();
    });
  };

  const handleTimeChange = (periodIndex, key, value) => {
    setSelectedTime((prevState) => ({
      ...prevState,
      [periodIndex]: {
        ...prevState[periodIndex],
        [key]: value,
      },
    }));
  };
  // Fix: Clear all selected cells, not just the first match per row/col
  const handleCellsChanged = (changes) => {
    
    // Clone the current demand type to safely track changes
    const updatedDemandType = JSON.parse(JSON.stringify(activeDemandType));
    const tierCount = activeDemandType.rate[0].options.tierBoundaries.length;
    const rowOffset = tierCount > 1 ? 2 : 1;
    let tariffNameChanged = false;
    const batchChanges = [];
    let rachetChanged = false;
    let rachetValue = null;
    let rachetTierId = null;

    // Process all direct changes
    changes.forEach(({ cell, row, col, value }) => {
      if (value === undefined) {
        return;
      }
      const periodIndex = row - rowOffset;
      if (periodIndex >= 0) {
        const tariff = updatedDemandType.rate[periodIndex];
        const tierIndex = tariff.options.tierBoundaries.findIndex(
          (a) => a.tierId === cell.tierId
        );
        const tier = tariff.options.tierBoundaries[tierIndex];

        if (cell.type === "tariffName") {
          updatePeriodName(periodIndex, value);
          tariffNameChanged = true;
        } else if (
          ["1", "2", "3", "4", "5", "6", "7", "8", "9"].includes(cell.type)
        ) {
          // Apply direct change
          if (tier[cell.type] && typeof tier[cell.type] === "object") {
            tier[cell.type].value = value;
            batchChanges.push({ periodIndex, tierIndex, key: cell.type, value });

            // Track Rachet changes for global sync
            if (cell.type === "7") {
              rachetChanged = true;
              rachetValue = value;
              rachetTierId = tier.tierId;
            }
          }
        }
      }
    });

    // If Rachet changed, sync globally across all seasons and demand types
    if (rachetChanged && batchUpdateRachetGlobally) {
      batchUpdateRachetGlobally(rachetTierId, rachetValue);
    } else if (batchChanges.length > 0 && typeof batchUpdateTierFractionsInDayGroup === "function") {
      // For non-Rachet changes, use the regular batch update
      batchUpdateTierFractionsInDayGroup(batchChanges);
    }

    // Regenerate grid from latest activeDemandType after tariff name change
    if (tariffNameChanged) {
      setTimeout(() => {
        // initalRenderer();
      }, 0);
    }
  };


  const [selectedTier, setSelectedTier] = useState(null);

  const handleCheckChange = (tierId, key, checked) => {
    updateSelectedSeasonAndDayGroup(tierId, key, checked);
  };

  const GlobalNumberEditor = (props) => {
    const inputRef = useRef();

    // Track the last valid value locally so invalid input can be reverted
    const [localValue, setLocalValue] = useState(props?.value ?? "");

    // Keep local state synchronized with external updates
    useEffect(() => {
      setLocalValue(props?.value ?? "");
    }, [props?.value]);

    // Always call onChange with the latest value before onCommit
    const commitValue = () => {
      // Always commit the latest valid local value
      if (props.onChange) props.onChange(localValue);
      if (props.onCommit) props.onCommit(localValue);
    };

    // Check if this is a percentage field (Rachet - key 7)
    const isPercentageField =
      props.cell.type === "7" ||
      props.cell.type === 7 ||
      props.cell.type === "3" ||
      props.cell.type === 3;

    return (
      <ProjectNumberInput
        type="text"
        ref={inputRef}
        variant="unstyled"
        value={localValue}
        onChange={(e) => {
          const val = e.target.value;
          const MAX_LEN = 64;
          const MAX_VALUE = 1e9;

          // Percentage validation for Rachet (0-100%)
          // Allows: 100 (no decimals), or 0-99 with up to 4 decimals
          if (isPercentageField) {
            const percentageRegex = /^(100(?!\.)|[0-9]{1,2})(\.[0-9]{1,4})?$/;

            const isValid =
              val === "" ||
              (val.length <= MAX_LEN && percentageRegex.test(val));

            if (isValid) {
              setLocalValue(val);
              props.onChange?.(val);
            } else {
              // Keep react-datasheet synchronized with the last valid value
              props.onChange?.(localValue);
            }
          } else {
            // Enforce max value of 1e9 while allowing intermediate numeric input states
            const isWithinRange = !Number.isFinite(Number(val)) || Math.abs(Number(val)) <= MAX_VALUE;

            // Limit decimal precision to 4 digits after the decimal point
            const hasValidPrecision = !val.includes(".") || val.split(".")[1].length <= 4;

            // Standard numeric validation for other fields
            const safeNumberRegex =
              /^-?(?:\d+(?:\.\d*)?|\.\d+)$/;

            const isValid =
              val === "" ||
              (val.length <= MAX_LEN &&
                safeNumberRegex.test(val) &&
                isWithinRange &&
                hasValidPrecision) ||
              props.cell.type === "tariffName";

            if (isValid) {
              setLocalValue(val);
              props.onChange?.(val);
            } else {
              // Keep react-datasheet synchronized with the last valid value
              props.onChange?.(localValue);
            }
          }
        }}
        onBlur={commitValue}
        onKeyDown={(e) => {
          if (e.key === "Tab" || e.key === "Enter") {
            e.preventDefault();
            commitValue();
          }
        }}
        style={{
          width: "100%",
          border: "none",
          height: "100%",
          textAlign: "center",
        }}
        styles={{
          root: { width: "100%", height: "100%" },
          wrapper: { width: "100%", height: "100%" },
          input: {
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
            border: "none",
            padding: 0,
            margin: 0,
            fontSize: "inherit",
            textAlign: "center",
            background: "transparent",
            "&:focus": {
              border: "none",
              boxShadow: "none",
            },
          },
        }}
        classNames={{ input: "tariff-number-editor-input" }}
        autoFocus
      />
    );
  };

  // Batch update function for tier fraction

  // Collect all used start and end times across all periods
  const usedTimes = new Set();
  activeDemandType.rate.forEach((tariff) => {
    tariff.timeSlots.forEach((slot) => {
      const start = `${slot.startTime.hour
        .toString()
        .padStart(2, "0")}:${slot.startTime.minute
        .toString()
        .padStart(2, "0")}`;
      const end = `${slot.endTime.hour
        .toString()
        .padStart(2, "0")}:${slot.endTime.minute.toString().padStart(2, "0")}`;
      usedTimes.add(start);
      usedTimes.add(end);
    });
  });

  // Build a Set of all times that fall within any existing slot's range
  const disabledTimes = new Set();
  const allSlotEndTimes = new Set();
  const allSlotStartTimes = new Set();
  activeDemandType.rate.forEach((tariff) => {
    tariff.timeSlots.forEach((slot) => {
      let startMinutes = slot.startTime.hour * 60 + slot.startTime.minute;
      let endMinutes = slot.endTime.hour * 60 + slot.endTime.minute;
      allSlotEndTimes.add(`${slot.endTime.hour.toString().padStart(2, "0")}:${slot.endTime.minute.toString().padStart(2, "0")}`);
      allSlotStartTimes.add(`${slot.startTime.hour.toString().padStart(2, "0")}:${slot.startTime.minute.toString().padStart(2, "0")}`);
      // Only disable times strictly inside the slot (exclude start and end)
      for (let min = startMinutes + 15; min < endMinutes; min += 15) {
        const hour = Math.floor(min / 60)
          .toString()
          .padStart(2, "0");
        const minute = (min % 60).toString().padStart(2, "0");
        disabledTimes.add(`${hour}:${minute}`);
      }
    });
  });

  // When selecting an end time, disable all times that are already used as an end time in any slot
  const isEndTimeSelect = (rowIndex, key) => key === "endTime";

  // Handler to open color modal
  const openColorModal = (rowIndex, currentColor) => {
    setColorModalRow(rowIndex);
    setTempColor(currentColor);
    setColorModalOpen(true);
  };

  // Handler to save color
  const saveColor = () => {
    if (colorModalRow !== null) {
      if (typeof updatePeriodColor === "function") {
        updatePeriodColor(colorModalRow, tempColor);
      } else {
        activeDemandType.rate[colorModalRow].periodColor = tempColor;
      }
    }
    setColorModalOpen(false);
    setColorModalRow(null);
  };

  return (
    <div className="data-sheet-container pl-2">
      <Modal
        opened={colorModalOpen}
        onClose={() => setColorModalOpen(false)}
        title={
          <span
            style={{ fontSize: "1rem", fontWeight: 500 }}
          >{`Change color of ${
            colorModalRow !== null && activeDemandType.rate[colorModalRow]?.name
              ? activeDemandType.rate[colorModalRow].name
              : ""
          }`}</span>
        }
        centered
        overlayProps={{ opacity: 0.55, blur: 3 }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 16,
          }}
        >
          <input
            type="color"
            value={tempColor}
            onChange={(e) => setTempColor(e.target.value)}
            style={{
              width: 64,
              height: 64,
              border: "none",
              background: "transparent",
              cursor: "pointer",
            }}
          />
          <button
            aria-label="save-demand-period-color"
            onClick={saveColor}
            style={{
              marginTop: 12,
              padding: "6px 18px",
              borderRadius: 4,
              border: "none",
              background: "#228be6",
              color: "#fff",
              fontWeight: 600,
              cursor: "pointer",
            }}
          >
            Save
          </button>
        </div>
      </Modal>
      <div className="zui-scroller" ref={scrollerRef}>
        <ReactDataSheet
          key={`${selectedSeason?.seasonId ?? "season"}-${activeDemandType?.demandTypeId ?? activeDemandTypeIndex}`}
          data={grid}
          valueRenderer={(cell) => formatNumericCell(cell.value)}
          dataRenderer={(cell) => formatNumericCell(cell.value)}
          onCellsChanged={handleCellsChanged}
          className={dynamicClass}
          dataEditor={GlobalNumberEditor}
        />
        <br />
        {/* ...existing context menu for data cells if needed... */}
      </div>
    </div>
  );
};

TariffDataSheet.propTypes = {
  activeDemandType: PropTypes.object,
  seasonsList: PropTypes.array,
  selectedSeason: PropTypes.object,
  activeDemandTypeIndex: PropTypes.number,
  updateTier: PropTypes.func,
  updateTimeSlot: PropTypes.func,
  callSnackbar: PropTypes.func,
  updatePeriodName: PropTypes.func,
  handleRemoveTimeSlots: PropTypes.func,
  currentDate: PropTypes.instanceOf(Date),
  updateTierFractionInDayGroup: PropTypes.func,
  updateSelectedSeasonAndDayGroup: PropTypes.func,
  allowBill: PropTypes.bool,
  allowPenalty: PropTypes.bool,
  batchUpdateTierFractionsInDayGroup: PropTypes.func,
  batchUpdateRachetGlobally: PropTypes.func,
  updatePeriodColor: PropTypes.func,
  isMasterContext: PropTypes.bool,
  isReadOnly: PropTypes.bool,
};

export default TariffDataSheet;

/* Add this CSS to TariffDataSheet.css:
.highlight-tier {
  animation: highlightFade 1.2s;
  background: #ffe066 !important;
}
@keyframes highlightFade {
  0% { background: #ffe066; }
  100% { background: inherit; }
}
*/
